import type { PlateEditor } from 'platejs/react';
import { ReactEditor } from 'slate-react';

export type CursorPosition = 'start' | 'end' | 'preserve';

/**
 * Reliably focuses the editor and sets cursor position.
 * This is the single source of truth for editor focus.
 *
 * The key insight: When we call DOM .focus(), the browser fires a 'selectionchange' event.
 * Slate listens to this (debounced) and syncs from DOM selection to Slate selection.
 * If the DOM selection was at start/empty, Slate will reset to start.
 *
 * Solution: Use ReactEditor.focus() which properly handles selection, or set both
 * Slate selection AND DOM selection to ensure they're in sync.
 *
 * @param editor - Plate editor instance.
 * @param position - Where to place cursor: 'start', 'end', or 'preserve' (default).
 */
export function focusEditor(editor: PlateEditor, position: CursorPosition = 'preserve'): void {
  /**
   * Use ReactEditor.focus() which properly handles focus + selection sync.
   * This ensures Slate's selection is preserved when focusing.
   */
  ReactEditor.focus(editor as unknown as ReactEditor);

  switch (position) {
    case 'start':
      editor.tf.select(editor.api.start([]));
      break;
    case 'end':
      editor.tf.select(editor.api.end([]));
      break;
    default:
      break;
  }
}
